import { POSITIONS, SquadInput, SquadPlayer } from '@fantasy/types';
import type { UnmatchedScreenshotPlayer } from './api.ts';

/**
 * Parses the paste-your-squad textarea.
 *
 * One player per line: `Name, POS, CLUB, price[, XI|BENCH][, C|V]`
 * e.g. `Saka, MID, ARS, 10.1, XI, C`
 *
 * Free-text upload is the v1 entry point, so parse errors need to name the line
 * that failed — a generic "invalid squad" makes a 15-line paste unfixable.
 */
export interface ParseResult {
  ok: boolean;
  players: SquadPlayer[];
  errors: string[];
}

export function parseSquadText(text: string): ParseResult {
  const errors: string[] = [];
  const players: SquadPlayer[] = [];

  const lines = text
    .split('\n')
    .map((line, index) => ({ line: line.trim(), number: index + 1 }))
    .filter(({ line }) => line.length > 0 && !line.startsWith('#'));

  for (const { line, number } of lines) {
    const parts = line.split(',').map((p) => p.trim());
    const [name, position, club, price, ...rest] = parts;

    if (parts.length < 4 || !name || !position || !club || !price) {
      errors.push(`Line ${number}: expected "Name, POS, CLUB, price" — got "${line}"`);
      continue;
    }

    const upperPosition = position.toUpperCase();
    if (!POSITIONS.includes(upperPosition as (typeof POSITIONS)[number])) {
      errors.push(`Line ${number}: "${position}" is not one of ${POSITIONS.join(', ')}`);
      continue;
    }

    const priceNumber = Number(price.replace(/[£m\s]/gi, ''));
    if (!Number.isFinite(priceNumber) || priceNumber <= 0) {
      errors.push(`Line ${number}: "${price}" is not a price like 10.1`);
      continue;
    }

    const flags = rest.map((r) => r.toUpperCase());
    const parsed = SquadPlayer.safeParse({
      name,
      position: upperPosition,
      club: club.toUpperCase(),
      price: Math.round(priceNumber * 10),
      isStarting: flags.includes('XI'),
      isCaptain: flags.includes('C'),
      isViceCaptain: flags.includes('V'),
    });

    if (!parsed.success) {
      errors.push(`Line ${number}: ${parsed.error.issues[0]?.message ?? 'invalid player'}`);
      continue;
    }
    players.push(parsed.data);
  }

  if (players.length !== 15 && errors.length === 0) {
    errors.push(`Expected 15 players, found ${players.length}.`);
  }

  return { ok: errors.length === 0, players, errors };
}

/**
 * Turns a partially-matched screenshot import back into paste-flow text, so
 * a failed import degrades into something editable instead of a dead end.
 * One well-formed line per matched player, round-tripping the same fields
 * `parseSquadText` reads back out. One placeholder line per unmatched
 * player, carrying whatever `nameFragment`/`club` was extracted alongside
 * an obviously-invalid position and price — `parseSquadText` will refuse to
 * parse it until it's fixed by hand, which is the point.
 */
export function buildRecoveryText(matched: SquadPlayer[], unmatched: UnmatchedScreenshotPlayer[]): string {
  const matchedLines = matched.map((player) => {
    const flags = [player.isStarting ? 'XI' : 'BENCH'];
    if (player.isCaptain) flags.push('C');
    else if (player.isViceCaptain) flags.push('V');
    return [player.name, player.position, player.club, (player.price / 10).toFixed(1), ...flags].join(', ');
  });

  const unmatchedLines = unmatched.map(
    (player) => `${player.nameFragment}, ???, ${player.club || '???'}, 0`,
  );

  return [...matchedLines, ...unmatchedLines].join('\n');
}

export function buildSquadInput(
  players: SquadPlayer[],
  meta: { gameweek: number; bank: number; freeTransfers: number; name?: string },
) {
  return SquadInput.parse({
    ...(meta.name?.trim() ? { name: meta.name.trim() } : {}),
    gameweek: meta.gameweek,
    players,
    bank: Math.round(meta.bank * 10),
    freeTransfers: meta.freeTransfers,
  });
}

export const EXAMPLE_SQUAD = `# Name, POS, CLUB, price, [XI|BENCH], [C|V]
Raya, GKP, ARS, 5.6, XI
Sanchez, GKP, CHE, 4.9
Gabriel, DEF, ARS, 6.3, XI
Van Dijk, DEF, LIV, 6.2, XI
Gvardiol, DEF, MCI, 6.1, XI
Trippier, DEF, NEW, 5.7
Mykolenko, DEF, EVE, 4.4
Salah, MID, LIV, 12.6, XI, C
Saka, MID, ARS, 10.1, XI, V
Palmer, MID, CHE, 10.6, XI
Gordon, MID, NEW, 7.5, XI
Rogers, MID, AVL, 5.4
Haaland, FWD, MCI, 15.1, XI
Isak, FWD, NEW, 8.6, XI
Wissa, FWD, BRE, 6.2, XI`;
